import LocalizedTextField from '@components/Customization/LocalizedTextField';
import { DescriptionField, NameField } from '@components/Customization/ReportDesigns/DesignFields';
import ToggleRow from '@components/Customization/ToggleRow';
import {
  locateBlock, locatePage, patchBlock, patchPage, patchSection, type DraftTarget, type ReportDesignDraft,
} from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, ButtonBase, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { buttonStyles, dangerHoverStyles } from '@pages/CustomPoiTypes.styles';
import {
  reportChromeSlotTypes, reportDesignLimits, reportTextLengths, type ReportCheckpointsSegment, type ReportChrome,
  type ReportChromeSlot, type ReportDesignPage, type ReportSummarySegment,
} from '@store/api/reportDesignEndpoints';
import { ArrowDownIcon, ArrowUpIcon, CopyIcon, TrashIcon } from 'react-line-awesome';
import { definitionOf } from './blockCatalog';
import BlockFieldControl from './BlockFieldControl';
import { inspectorTitleStyles, statusChipActiveStyles, statusChipStyles } from './designer.styles';
import { pageLabel } from './describe';
import ImageField, { type DesignImages } from './ImageField';
import {
  CHECKPOINT_OPTIONS, CHECKPOINT_TEMPLATES, DEFAULT_ACCENT, SIDES, SLOT_LABELS, STATUSES, SUMMARY_TEMPLATES,
} from './options';

/** Applies an edit to the draft. */
type Change = (edit: (draft: ReportDesignDraft) => ReportDesignDraft) => void;

/**
 * What the inspector can ask of the designer beyond editing the draft. Duplicating a block
 * and deleting a block or a page change what is selected, so the inspector is drawn anew and
 * the pressed button goes with the old one: the designer then hands the keyboard's focus on
 * to a part of the new one, which the inspector marks with `data-inspector` (`useHandover`).
 */
export interface InspectorActions {
  duplicateBlock: (blockId: string) => void;
  removeBlock: (blockId: string) => void;
  movePage: (pageId: string, to: number) => void;
  removePage: (pageId: string) => void;
}

interface Props {
  draft: ReportDesignDraft;
  selection: DraftTarget;
  canEdit: boolean;
  images: DesignImages;
  change: Change;
  actions: InspectorActions;
}

// The title is what takes the keyboard's focus after a deletion made from the inspector, and
// after a Duplicate that has filled the page (`useHandover`). Code can put the focus on it,
// and Tab does not stop at it.
const Heading: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <Box>
    <Typography sx={inspectorTitleStyles} tabIndex={-1} data-inspector="title">{title}</Typography>
    {note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{note}</Typography>}
  </Box>
);

interface ChoiceProps {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  help?: string;
  disabled: boolean;
}

/** A choice among a few named options. */
const Choice: React.FC<ChoiceProps> = ({ label, value, options, onChange, help, disabled }) => {
  const { t } = useLocalization();

  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      helperText={help}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    >
      {options.map((option) => <MenuItem key={option.value} value={option.value}>{t(option.label)}</MenuItem>)}
    </TextField>
  );
};

/**
 * The settings of whatever is selected, on the canvas or in the sections list. Every control
 * writes the draft through `change`; nothing here keeps a value of its own, so Discard and a
 * newer saved copy show at once.
 */
const Inspector: React.FC<Props> = ({ draft, selection, canEdit, images, change, actions }) => {
  const { t } = useLocalization();
  const disabled = !canEdit;

  const details = () => (
    <>
      <Heading title={t('ReportDesign_Section_Details')} />
      <NameField
        autoFocus={false}
        value={draft.name}
        onChange={(name) => change((current) => ({ ...current, name }))}
        disabled={disabled}
      />
      <DescriptionField
        value={draft.description}
        onChange={(description) => change((current) => ({ ...current, description }))}
        disabled={disabled}
      />
    </>
  );

  const theme = () => {
    const set = (changes: Partial<ReportDesignDraft['theme']>) =>
      change((current) => patchSection(current, 'theme', changes));

    return (
      <>
        <Heading title={t('ReportDesign_Section_Theme')} />
        <LocalizedTextField
          optional
          carriedHint={t('ReportDesign_Text_OtherLanguage')}
          label={t('ReportDesign_Field_BrandName')}
          helperText={t('ReportDesign_Field_BrandName_Help')}
          value={draft.theme.brandName ?? {}}
          onChange={(brandName) => set({ brandName })}
          maxLength={reportTextLengths.brand}
          disabled={disabled}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            type="color"
            size="small"
            label={t('ReportDesign_Field_Accent')}
            value={draft.theme.accentColor ?? DEFAULT_ACCENT}
            onChange={(event) => set({ accentColor: event.target.value })}
            disabled={disabled}
            sx={{ width: 120 }}
          />
          <Button
            size="small"
            color="inherit"
            sx={buttonStyles}
            disabled={disabled || draft.theme.accentColor === undefined}
            onClick={() => set({ accentColor: undefined })}
          >
            {t('Customization_Designer_Default')}
          </Button>
        </Stack>
        <ImageField
          label={t('ReportDesign_Logo')}
          help={t('ReportDesign_Logo_Help')}
          value={draft.theme.logo}
          onChange={(logo) => set({ logo })}
          images={images}
          disabled={disabled}
        />
      </>
    );
  };

  const chrome = (kind: 'header' | 'footer') => {
    const strip: ReportChrome = draft[kind];
    const set = (changes: Partial<ReportChrome>) => change((current) => patchSection(current, kind, changes));

    return (
      <>
        <Heading title={t(kind === 'header' ? 'ReportDesign_Section_Header' : 'ReportDesign_Section_Footer')} />
        <ToggleRow
          label={t('ReportDesign_Chrome_Enabled')}
          description={t('ReportDesign_Chrome_Help')}
          checked={strip.enabled}
          onChange={(enabled) => set({ enabled })}
          disabled={disabled}
        />
        {SIDES.map(({ side, label }) => {
          const slot = strip[side];

          return (
            <Stack key={side} spacing={1.5}>
              <Choice
                label={t(label)}
                value={slot?.type ?? 'none'}
                options={reportChromeSlotTypes.map((type) => ({ value: type, label: SLOT_LABELS[type] }))}
                // The text goes with the one type that prints it.
                onChange={(type) => set({
                  [side]: { type, ...(type === 'text' && slot?.text ? { text: slot.text } : {}) } as ReportChromeSlot,
                })}
                disabled={disabled}
              />
              {slot?.type === 'text' && (
                <LocalizedTextField
                  optional
                  carriedHint={t('ReportDesign_Text_OtherLanguage')}
                  label={t('ReportDesign_Field_Text')}
                  helperText={t('ReportDesign_Placeholders')}
                  value={slot.text ?? {}}
                  onChange={(text) => set({ [side]: { ...slot, text } })}
                  maxLength={reportTextLengths.line}
                  disabled={disabled}
                />
              )}
            </Stack>
          );
        })}
      </>
    );
  };

  const summary = () => {
    const segment = draft.summary;
    const set = (changes: Partial<ReportSummarySegment>) => change((current) => patchSection(current, 'summary', changes));
    const toggle = (key: 'showVerdict' | 'showCompletion' | 'showBar' | 'showCounts', label: string) => (
      <ToggleRow
        key={key}
        label={t(label)}
        // On unless switched off: what the report does with a design that does not say.
        checked={segment[key] ?? true}
        onChange={(checked) => set({ [key]: checked })}
        disabled={disabled}
      />
    );

    return (
      <>
        <Heading title={t('Common_Summary')} note={t('ReportDesign_Summary_Help')} />
        <Choice
          label={t('ReportDesign_Field_Template')}
          value={segment.template}
          options={SUMMARY_TEMPLATES}
          onChange={(template) => set({ template: template as ReportSummarySegment['template'] })}
          disabled={disabled}
        />
        {segment.template !== 'none' && (
          <LocalizedTextField
            optional
            carriedHint={t('ReportDesign_Text_OtherLanguage')}
            label={t('Common_Title')}
            placeholder={t('Common_Summary')}
            value={segment.title ?? {}}
            onChange={(title) => set({ title })}
            maxLength={reportTextLengths.line}
            disabled={disabled}
          />
        )}
        {segment.template === 'dashboard' && [
          toggle('showVerdict', 'ReportDesign_Summary_Verdict'),
          toggle('showCompletion', 'Common_Completion'),
          toggle('showBar', 'ReportDesign_Summary_Bar'),
          toggle('showCounts', 'ReportDesign_Summary_Counts'),
        ]}
      </>
    );
  };

  const checkpoints = () => {
    const segment = draft.checkpoints;
    const offers = (option: string) => (CHECKPOINT_OPTIONS[segment.template] ?? []).includes(option);
    const set = (changes: Partial<ReportCheckpointsSegment>) =>
      change((current) => patchSection(current, 'checkpoints', changes));

    // The cards print a step's photos as thumbnails or not at all, the pages at full width or
    // not at all. The switch shows what the chosen template does with the stored value.
    const inCards = segment.template === 'cards';
    const photosOn = inCards ? (segment.photos ?? 'thumbs') === 'thumbs' : segment.photos !== 'none';
    const statuses = segment.statuses ?? [];

    return (
      <>
        <Heading title={t('Common_Checkpoints')} />
        <Choice
          label={t('ReportDesign_Field_Template')}
          value={segment.template}
          options={CHECKPOINT_TEMPLATES}
          onChange={(template) => set({ template: template as ReportCheckpointsSegment['template'] })}
          disabled={disabled}
        />
        {offers('title') && (
          <LocalizedTextField
            optional
            carriedHint={t('ReportDesign_Text_OtherLanguage')}
            label={t('Common_Title')}
            placeholder={t('Inspection_Checkpoints')}
            value={segment.title ?? {}}
            onChange={(title) => set({ title })}
            maxLength={reportTextLengths.line}
            disabled={disabled}
          />
        )}
        {offers('filter') && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('ReportDesign_Checkpoints_Statuses')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('ReportDesign_Checkpoints_Statuses_Help')}
            </Typography>
            <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
              {STATUSES.map(({ value, label }) => {
                const on = statuses.includes(value);
                const next = on ? statuses.filter((status) => status !== value) : [...statuses, value];

                return (
                  <ButtonBase
                    key={value}
                    aria-pressed={on}
                    disabled={disabled}
                    sx={on ? [statusChipStyles, statusChipActiveStyles] : statusChipStyles}
                    // None chosen is the same list as all four, and says it in fewer bytes.
                    onClick={() => set({ statuses: next.length ? next : undefined })}
                  >
                    {t(label)}
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>
        )}
        {offers('photos') && (
          <ToggleRow
            label={t('ReportDesign_Checkpoints_Photos')}
            checked={photosOn}
            onChange={(checked) => set({ photos: checked ? (inCards ? 'thumbs' : 'large') : 'none' })}
            disabled={disabled}
          />
        )}
        {offers('comments') && (
          <ToggleRow
            label={t('ReportDesign_Checkpoints_Comments')}
            checked={segment.showComments ?? true}
            onChange={(showComments) => set({ showComments })}
            disabled={disabled}
          />
        )}
        {offers('customFields') && (
          <ToggleRow
            label={t('ReportDesign_Checkpoints_CustomFields')}
            checked={segment.showCustomFields ?? true}
            onChange={(showCustomFields) => set({ showCustomFields })}
            disabled={disabled}
          />
        )}
        {offers('descriptions') && (
          <ToggleRow
            label={t('ReportDesign_Checkpoints_Descriptions')}
            checked={segment.showDescriptions ?? false}
            onChange={(showDescriptions) => set({ showDescriptions })}
            disabled={disabled}
          />
        )}
        {offers('dates') && (
          <ToggleRow
            label={t('ReportDesign_Checkpoints_Dates')}
            checked={segment.showDates ?? false}
            onChange={(showDates) => set({ showDates })}
            disabled={disabled}
          />
        )}
        {segment.template !== 'none' && (
          <ToggleRow
            label={t('Common_Notes')}
            description={t('ReportDesign_Checkpoints_Notes_Help')}
            checked={segment.includeNotes ?? false}
            onChange={(includeNotes) => set({ includeNotes })}
            disabled={disabled}
          />
        )}
      </>
    );
  };

  const page = (pageId: string) => {
    const at = locatePage(draft, pageId);

    if (!at) return null;

    const set = (changes: Partial<Pick<ReportDesignPage, 'background' | 'verticalAlign'>>) =>
      change((current) => patchPage(current, pageId, changes));

    // A cover is centred and a closing page starts at the top, unless the page says otherwise.
    const align = at.page.verticalAlign ?? (at.section === 'covers' ? 'center' : 'top');
    const last = draft[at.section].length - 1;

    return (
      <>
        <Heading title={pageLabel(draft, pageId, t)} />
        <Choice
          label={t('ReportDesign_Field_Background')}
          value={at.page.background ?? 'white'}
          options={[
            { value: 'white', label: 'ReportDesign_Option_White' },
            { value: 'navy', label: 'ReportDesign_Option_Navy' },
          ]}
          onChange={(background) => set({ background: background as ReportDesignPage['background'] })}
          help={at.page.background === 'navy' && at.section === 'closings' ? t('ReportDesign_Page_NavyHelp') : undefined}
          disabled={disabled}
        />
        <Choice
          label={t('ReportDesign_Field_VerticalAlign')}
          value={align}
          options={[
            { value: 'top', label: 'Common_Top' },
            { value: 'center', label: 'ReportDesign_Option_Center' },
            { value: 'bottom', label: 'Common_Bottom' },
          ]}
          onChange={(verticalAlign) => set({ verticalAlign: verticalAlign as ReportDesignPage['verticalAlign'] })}
          help={align === 'top' ? undefined : t('ReportDesign_Page_OnePageHelp')}
          disabled={disabled}
        />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small" color="inherit" startIcon={<ArrowUpIcon />} sx={buttonStyles}
            disabled={disabled || at.index === 0}
            onClick={() => actions.movePage(pageId, at.index - 1)}
          >
            {t('Customization_Designer_MoveUp')}
          </Button>
          <Button
            size="small" color="inherit" startIcon={<ArrowDownIcon />} sx={buttonStyles}
            disabled={disabled || at.index === last}
            onClick={() => actions.movePage(pageId, at.index + 1)}
          >
            {t('Customization_Designer_MoveDown')}
          </Button>
          <Button
            size="small" startIcon={<TrashIcon />} sx={{ ...buttonStyles, ...dangerHoverStyles }}
            disabled={disabled}
            onClick={() => actions.removePage(pageId)}
          >
            {t('ReportDesign_Page_Delete')}
          </Button>
        </Stack>
      </>
    );
  };

  const block = (blockId: string) => {
    const at = locateBlock(draft, blockId);

    if (!at) return null;

    const definition = definitionOf(at.block.type);
    const props: Record<string, unknown> = at.block.props ?? {};

    return (
      <>
        <Heading
          title={definition ? t(definition.label) : at.block.type}
          // A block of a later version is kept as it is and drawn by a server that knows it.
          note={definition ? t(definition.description) : t('ReportDesign_Block_Unknown')}
        />
        {definition?.fields.filter((field) => !field.when || field.when(props)).map((field) => (
          <BlockFieldControl
            key={field.key}
            field={field}
            props={props}
            onPatch={(changes) => change((current) => patchBlock(current, blockId, changes))}
            images={images}
            disabled={disabled}
          />
        ))}
        <Stack direction="row" spacing={1}>
          {/* Marked for the focus to come back to it on the copy's inspector, where it is pressed again (`useHandover`). */}
          <Button
            size="small" color="inherit" startIcon={<CopyIcon />} sx={buttonStyles}
            data-inspector="duplicate"
            disabled={disabled || at.page.blocks.length >= reportDesignLimits.blocksPerPage}
            onClick={() => actions.duplicateBlock(blockId)}
          >
            {t('Customization_Duplicate')}
          </Button>
          <Button
            size="small" startIcon={<TrashIcon />} sx={{ ...buttonStyles, ...dangerHoverStyles }}
            disabled={disabled}
            onClick={() => actions.removeBlock(blockId)}
          >
            {t('Common_Delete')}
          </Button>
        </Stack>
      </>
    );
  };

  const body = () => {
    switch (selection.kind) {
      case 'details': return details();
      case 'theme': return theme();
      case 'header':
      case 'footer': return chrome(selection.kind);
      case 'summary': return summary();
      case 'checkpoints': return checkpoints();
      case 'page': return page(selection.id);
      default: return block(selection.id);
    }
  };

  return <Stack spacing={2} sx={{ p: 2 }}>{body()}</Stack>;
};

export default Inspector;
